import { toast } from "sonner";

/**
 * Fullscreen-safe confirmation dialog.
 *
 * The native `window.confirm()` and `window.alert()` are guaranteed to
 * **exit fullscreen mode** on Chromium and Firefox for security reasons —
 * this includes fullscreen on ANY tab of the same browser process, even
 * a different Big Screen window than the one triggering the confirm.
 *
 * This helper uses `sonner`'s promise-based action toast instead, which
 * renders as a non-modal overlay and does NOT touch the browser's
 * fullscreen state. The confirmation is fully async and returns a
 * Promise<boolean> — resolves true on confirm, false on dismiss/cancel.
 *
 * Only use this for confirmations that could happen while a Big Screen
 * is being displayed to the public (reset, forfeit, cancel action, ...).
 * For destructive admin actions that never occur mid-event, plain
 * window.confirm is fine.
 *
 * @param {string} message - The confirmation question
 * @param {object} [opts]
 * @param {string} [opts.confirmLabel="OK"]
 * @param {string} [opts.cancelLabel="Annuler"]
 * @param {number} [opts.duration=15000] - Auto-dismiss timeout (ms)
 * @returns {Promise<boolean>}
 */
export function safeConfirm(message, opts = {}) {
  const {
    confirmLabel = "OK",
    cancelLabel = "Annuler",
    duration = 15000,
  } = opts;
  return new Promise((resolve) => {
    let settled = false;
    const done = (result) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    toast.warning(message, {
      duration,
      action: { label: confirmLabel, onClick: () => done(true) },
      cancel: { label: cancelLabel, onClick: () => done(false) },
      onDismiss: () => done(false),
      onAutoClose: () => done(false),
    });
  });
}

export default safeConfirm;
